/**
 * A feedback request's due date, read as a South African calendar day.
 *
 * performance-service keeps ``due_date`` in a column without a time zone,
 * holding UTC, and sends it back without an offset: a request due 29 Sep
 * comes back as ``"2026-09-29T00:00:00"``. ``new Date()`` reads a string
 * without an offset as LOCAL time, so in South Africa the edit dialog showed
 * that request as due 28 Sep, and the pending list called it past its
 * recommended date all day on 29 Sep.
 */
import { sastDay, sastToday } from '@/components/admin/lifecycle/spellCoverage'
import { parseApiDate, sastCalendarDate } from '@/lib/apiDate'

/** The South African day (YYYY-MM-DD) of a stored due date, or null. */
export function feedbackDueDay(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null
  const instant = parseApiDate(dueDate)
  if (Number.isNaN(instant.getTime())) return null
  return sastDay(instant.toISOString())
}

/**
 * A stored due date as shown in the pending list ("Recommended by"): its
 * South African day, or null. Read as local time, a campaign deadline
 * between 00:00 and 02:00 showed the day before.
 */
export function feedbackDueDateLabel(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null
  const day = sastCalendarDate(dueDate)
  return Number.isNaN(day.getTime()) ? null : day.toLocaleDateString()
}

/** Past the recommended date only once that South African day has ended. */
export function isPastRecommendedDate(
  dueDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const day = feedbackDueDay(dueDate)
  return day !== null && day < sastToday(now)
}
